/* ============================================================================
 * Howdy Brewer — Homepage v2 JS  (assets/home-howdy-v2.js)
 * ----------------------------------------------------------------------------
 * Vanilla, IIFE-wrapped, idempotent. Loaded by multiple sections (the hero
 * loads it for the page, the recently-viewed section loads it as a hard
 * dependency); the sentinel (window.__hbHomeJs) prevents double-init when
 * it's included more than once.
 *
 * Responsibilities:
 *   1. fireGA4Event() — the design-system helper (transport: beacon,
 *      event_callback, event_timeout) so click → navigate races don't
 *      eat the event.
 *   2. Auto-binds [data-ga4-event] across the page, propagating any
 *      [data-*] attrs on the element as event params (camelCased).
 *   3. Recently-viewed rail: reads localStorage[storage_key], renders
 *      real cards, hides the section when empty.
 *
 * Push-to-localStorage from PDP visits is OUT OF SCOPE here — that lives
 * in the PDP template's pdp-howdy.js.  Reference contract:
 *
 *     localStorage.setItem('hb_recent', JSON.stringify([
 *       { handle, title, vendor, price, image, url, ts },  // newest first
 *       ...
 *     ].slice(0, 10)))
 *
 * Where:
 *   handle  = product.handle               (string)
 *   title   = product.title                (string)
 *   vendor  = product.vendor               (string, may be empty)
 *   price   = product.price | money        (string, pre-formatted)
 *   image   = product.featured_image       (string URL, ≥ 240px wide)
 *   url     = product.url                  (string, /products/{handle})
 *   ts      = Date.now()                   (number, ms epoch)
 * ============================================================================ */
(function () {
  'use strict';

  if (window.__hbHomeJs) return;
  window.__hbHomeJs = true;

  /* --------------------------------------------------------------------------
   * GA4 helper
   * ------------------------------------------------------------------------ */
  function fireGA4Event(name, params) {
    if (typeof window.gtag !== 'function') return;
    var payload = Object.assign(
      {
        event_callback: function () {},
        event_timeout: 1000,
        transport_type: 'beacon'
      },
      params || {}
    );
    try { window.gtag('event', name, payload); } catch (e) { /* swallow */ }
  }
  // Expose for other scripts (PDP, cart) that want the same signature.
  window.hbFireGA4Event = window.hbFireGA4Event || fireGA4Event;

  /* --------------------------------------------------------------------------
   * Delegated [data-ga4-event] binding
   *
   * Any element (anchor, button, form) with data-ga4-event="event_name"
   * fires an event on click / submit. Other data-* attrs are forwarded
   * as params (data-product-handle → product_handle).
   * ------------------------------------------------------------------------ */
  function collectDataParams(el) {
    var out = {};
    if (!el || !el.attributes) return out;
    for (var i = 0; i < el.attributes.length; i++) {
      var a = el.attributes[i];
      if (a.name.indexOf('data-') !== 0) continue;
      if (a.name === 'data-ga4-event') continue;
      var key = a.name.slice(5).replace(/-/g, '_');
      out[key] = a.value;
    }
    return out;
  }

  function findGA4Target(node) {
    var el = node;
    var hops = 0;
    while (el && el.nodeType === 1 && hops < 6) {
      if (el.dataset && el.dataset.ga4Event) return el;
      el = el.parentNode;
      hops++;
    }
    return null;
  }

  document.addEventListener(
    'click',
    function (e) {
      var target = findGA4Target(e.target);
      if (!target) return;
      var name = target.dataset.ga4Event;
      var params = collectDataParams(target);
      // Annotate location for downstream segmentation
      if (!params.page_path) params.page_path = window.location.pathname;
      fireGA4Event(name, params);
    },
    true
  );

  // Hero search submit (form-level, not click)
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.dataset || !form.dataset.ga4Event) return;
      var name = form.dataset.ga4Event;
      var params = collectDataParams(form);
      var input = form.querySelector('input[name="q"]');
      if (input) params.search_term = (input.value || '').trim();
      fireGA4Event(name, params);
    },
    true
  );

  /* --------------------------------------------------------------------------
   * Recently Viewed rail
   * ------------------------------------------------------------------------ */
  function moneyEscape(s) {
    if (s == null) return '';
    return String(s);
  }
  function htmlEscape(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function renderRecentCard(item) {
    var url = htmlEscape(item.url || ('/products/' + (item.handle || '')));
    var title = htmlEscape(item.title || '');
    var vendor = htmlEscape(item.vendor || '');
    var price = moneyEscape(item.price || '');
    var image = htmlEscape(item.image || '');

    return (
      '<a class="hb-home-v2-card" href="' + url + '"' +
        ' data-ga4-event="recently_viewed_click"' +
        ' data-product-handle="' + htmlEscape(item.handle || '') + '">' +
        '<div class="hb-home-v2-card-img">' +
          (image
            ? '<img src="' + image + '" alt="' + title + '" width="240" height="240" loading="lazy">'
            : '<span class="hb-home-v2-card-img-placeholder" aria-hidden="true"></span>') +
        '</div>' +
        '<div class="hb-home-v2-card-text">' +
          (vendor ? '<span class="hb-home-v2-card-vendor">' + vendor + '</span>' : '') +
          '<h4 class="hb-home-v2-card-title">' + title + '</h4>' +
          (price ? '<div class="hb-home-v2-card-price"><span class="hb-home-v2-card-price-now">' + price + '</span></div>' : '') +
        '</div>' +
      '</a>'
    );
  }

  function readRecent(key) {
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      // Filter malformed entries
      return arr.filter(function (i) { return i && typeof i === 'object' && i.handle; });
    } catch (e) {
      return [];
    }
  }

  function initRecentlyViewed() {
    var shells = document.querySelectorAll('[data-recently-viewed-shell]');
    if (!shells.length) return;

    Array.prototype.forEach.call(shells, function (shell) {
      var key = shell.dataset.storageKey || 'hb_recent';
      var max = parseInt(shell.dataset.maxCards || '5', 10);
      if (!(max > 0)) max = 5;

      var items = readRecent(key).slice(0, max);
      if (!items.length) {
        shell.hidden = true;
        return;
      }

      var row = shell.querySelector('[data-recently-viewed-row]');
      if (!row) return;
      row.innerHTML = items.map(renderRecentCard).join('');
      row.dataset.count = String(items.length);

      shell.hidden = false;

      // Wire the optional clear-history button
      var clearBtn = shell.querySelector('[data-recently-viewed-clear]');
      if (clearBtn) {
        clearBtn.hidden = false;
        clearBtn.addEventListener(
          'click',
          function () {
            try { window.localStorage.removeItem(key); } catch (e) { /* swallow */ }
            shell.hidden = true;
          },
          { once: true }
        );
      }
    });
  }

  /* --------------------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRecentlyViewed);
  } else {
    initRecentlyViewed();
  }
})();
